import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Sparkles, RefreshCw, Minimize2, Maximize2, Mic, Plus, Image as ImageIcon, FileText, Link as LinkIcon, Activity } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { ConfirmationModal } from '../ui/ConfirmationModal';

export const AiChat = ({
    isOpen = true,
    onClose,
    isEmbedded = false,
    isExpanded = false,
    onExpandToggle,
    extraContent = null
}) => {
    const { toast } = useToast();
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hallo! Ich bin dein PIM-Assistent. Ich kann dir helfen, Produkte zu finden, technische Fragen zu beantworten oder Daten zusammenzufassen. Wie kann ich dir heute helfen?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isUploadMenuOpen, setIsUploadMenuOpen] = useState(false);
    const [lastEntityContext, setLastEntityContext] = useState(null); // Store context for follow-up questions

    // --- Status Polling ---
    const [modelStatus, setModelStatus] = useState('IDLE');
    const statusInterval = useRef(null);

    useEffect(() => {
        if (isLoading) {
            statusInterval.current = setInterval(async () => {
                try {
                    const res = await fetch('/api/ai/status');
                    const data = await res.json();
                    // If any model is in non-ready state?
                    // For now, allow the UI to just show the "Loading..." text if isLoading is true. 
                    // The specific "modelStatus" state might be redundant if we just show the message in the isLoading block.
                    // But per req: "When Backend Status = LOADING report...".
                    // So let's check properly.
                    if (data.success && data.models && Array.isArray(data.models)) {
                        // Find if any model is LOADING (heuristic or explicit)
                        // Our OllamaStatusService returns models. 
                        // If we don't have explicit "LOADING" status from `ollama ps`, 
                        // we rely on LLMSwitch internal state? No, LLMSwitch just logs it.
                        // But if it's NOT in `ps`, it's loading or idle.
                        // If isLoading is true (User sent request), and model is NOT in `ps`, it is likely doing cold boot.
                        // So: if isLoading && (model not found in ps OR model size_vram=0) -> Show "Warmup".
                    }
                } catch (e) { }
            }, 2000);
        } else {
            clearInterval(statusInterval.current);
            setModelStatus('IDLE');
        }
        return () => clearInterval(statusInterval.current);
    }, [isLoading]);
    // --- End Status Polling ---

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const res = await fetch('/api/ai/ask', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    question: userMessage.content,
                    previous_context: lastEntityContext
                })
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to get response', { cause: errData });
            }

            const data = await res.json();

            // Update context for next turn
            if (data.entity_context) {
                setLastEntityContext(data.entity_context);
            }

            const assistantMessage = {
                role: 'assistant',
                content: data.answer,
                context: data.context,
                sources: data.sources,
                traceId: data.traceId,
                trace: data.debug_trace
            };
            setMessages(prev => [...prev, assistantMessage]);

        } catch (error) {
            console.error("AI Error:", error);
            const traceId = error.cause?.traceId || `err_${Date.now()}`;

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Entschuldigung, ich konnte deine Anfrage nicht verarbeiten.",
                isError: true,
                traceId: traceId,
                originalQuery: userMessage.content // Save for retry
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetry = async (originalQuery) => {
        // Remove the error message
        setMessages(prev => prev.slice(0, -1));
        setInput(originalQuery);
        // Auto-send would require extraction of logic, for now just fill input
        // Or specific retry logic:
        // handleSend(); // But input state might not be updated yet due to closure.
        // Better:
        // just pre-fill and focus
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSyncRequest = () => {
        setShowSyncConfirm(true);
    };

    const executeSync = async () => {
        setShowSyncConfirm(false);
        const loadingMsg = { role: 'assistant', content: "🔄 Starting knowledge base synchronization..." };
        setMessages(prev => [...prev, loadingMsg]);

        try {
            const res = await fetch('/api/ai/sync', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json();

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: data.success
                    ? `✅ Sync complete! I have learned about ${data.count} products.`
                    : `❌ Sync failed: ${data.error}`
            }]);
        } catch (error) {
            console.error("Sync failed:", error);
            setMessages(prev => [...prev, { role: 'assistant', content: "❌ Sync request failed." }]);
        }
    };

    const handleSpeech = () => {
        if (!('webkitSpeechRecognition' in window)) {
            toast.warning("Speech recognition is not supported in this browser.");
            return;
        }

        const recognition = new window.webkitSpeechRecognition();
        recognition.lang = "de-DE";
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInput(prev => prev + (prev ? ' ' : '') + transcript);
        };

        recognition.start();
    };

    const handleFileUploadTrigger = () => {
        fileInputRef.current.click();
        setIsUploadMenuOpen(false);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setMessages(prev => [...prev, { role: 'user', content: `📎 Uploading: ${file.name}...` }]);

        try {
            const res = await fetch('/api/ai/upload', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (data.success) {
                setMessages(prev => [...prev, { role: 'assistant', content: `✅ Received ${file.name}. (Stored at ${data.path}). I can now use this context.` }]);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', content: `❌ Upload failed: ${data.error}` }]);
            }
        } catch (err) {
            console.error(err);
            setMessages(prev => [...prev, { role: 'assistant', content: `❌ Upload error.` }]);
        }
    };

    if (!isOpen && !isEmbedded) return null;

    // Styles
    const containerClasses = isEmbedded
        ? "h-full flex flex-col bg-transparent relative"
        : `fixed z-50 transition-all duration-300 ease-in-out bg-[var(--color-bg-panel)] border border-[var(--color-border)] shadow-2xl rounded-xl flex flex-col overflow-hidden ${isExpanded ? 'top-10 left-10 right-10 bottom-10' : 'bottom-6 right-6 w-[400px] h-[600px]'}`;

    const containerStyle = isEmbedded ? {} : { backdropFilter: 'blur(12px)' };

    return (
        <div className={containerClasses} style={containerStyle}>
            {/* Header - Conditional render */}
            {!isEmbedded && (
                <div className="h-14 bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-b border-[var(--color-border)] flex items-center justify-between px-4 shrink-0">
                    <div className="flex items-center gap-2 text-white font-medium">
                        <Sparkles size={18} className="text-purple-300" />
                        <span>PIM AI Agent</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={handleSyncRequest} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition" title="Update Knowledge Base">
                            <RefreshCw size={16} />
                        </button>
                        {onExpandToggle && (
                            <button onClick={onExpandToggle} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition">
                                {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                            </button>
                        )}
                        {onClose && (
                            <button onClick={onClose} className="p-2 text-white/70 hover:text-red-400 hover:bg-white/10 rounded-full transition">
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Embedded Header (Optional) */}
            {isEmbedded && (
                <>
                    <h3 className="absolute top-0 left-1 text-lg font-bold text-white z-20">AI Assistant</h3>
                    <button
                        onClick={handleSyncRequest}
                        className="absolute top-2 right-12 z-20 p-1.5 rounded bg-purple-900/30 text-purple-300 border border-purple-700 hover:bg-purple-900/50 hover:text-white transition-colors"
                        title="Sync"
                    >
                        <RefreshCw size={14} />
                    </button>
                    {/* Spacer to push content down from absolute header */}
                    <div className="h-10 shrink-0" />
                </>
            )}

            {/* Extra Content (Drag Drop Zone) */}
            {extraContent && (
                <div className="shrink-0">
                    {extraContent}
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/20">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'assistant' ? 'bg-purple-900/50 text-purple-300' : 'bg-stone-700 text-stone-300'}`}>
                            {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
                        </div>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.role === 'user'
                            ? 'bg-blue-600 text-white rounded-tr-sm'
                            : 'bg-stone-800/80 text-stone-200 border border-stone-700 rounded-tl-sm'
                            }`}>
                            <div className="whitespace-pre-wrap">{msg.content}</div>

                            {/* Error Retry */}
                            {msg.isError && (
                                <div className="mt-2 flex flex-col gap-2">
                                    <div className="text-xs text-red-300">Error ID: {msg.traceId}</div>
                                    <button
                                        onClick={() => { setInput(msg.originalQuery); }}
                                        className="self-start px-3 py-1 bg-red-900/40 hover:bg-red-900/60 text-red-200 text-xs rounded border border-red-800 transition-colors flex items-center gap-1"
                                    >
                                        <RefreshCw size={12} /> Retry
                                    </button>
                                </div>
                            )}

                            {/* Sources Display */}
                            {msg.sources && msg.sources.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-white/10 text-xs text-stone-400">
                                    <div className="font-semibold mb-1 text-purple-400 flex items-center gap-1">
                                        <FileText size={12} /> Quellen ({msg.sources.length}):
                                    </div>
                                    <ul className="space-y-1.5">
                                        {msg.sources.map((src, i) => (
                                            <li key={i} className="flex flex-col bg-black/20 p-1.5 rounded border border-white/5">
                                                <div className="flex justify-between text-stone-300 font-medium">
                                                    <span className="truncate" title={src.filename}>{src.filename}</span>
                                                    <span className="text-[10px] uppercase bg-stone-700 px-1 rounded text-stone-400">{src.type}</span>
                                                </div>
                                                {src.snippet && (
                                                    <div className="text-stone-500 italic mt-0.5 line-clamp-2">
                                                        "{src.snippet}"
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Fallback Legacy Context Display (if sources empty but context exists) */}
                            {(!msg.sources || msg.sources.length === 0) && msg.context && msg.context.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-white/10 text-xs text-stone-400">
                                    <div className="font-semibold mb-1 text-purple-400">Kontext:</div>
                                    <ul className="list-disc pl-4 space-y-1">
                                        {msg.context.slice(0, 3).map((ctx, i) => (
                                            <li key={i} className="line-clamp-2">{typeof ctx === 'string' ? ctx.split('\n')[0] : JSON.stringify(ctx)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* DEBUG TRACE PANEL */}
                            {msg.trace && (
                                <details className="mt-3 pt-2 border-t border-white/10 group">
                                    <summary className="text-xs text-stone-500 font-mono cursor-pointer hover:text-purple-400 flex items-center gap-2 select-none">
                                        <Activity size={10} />
                                        DEBUG TRACE ({msg.trace.totalDuration}ms)
                                    </summary>
                                    <div className="mt-2 pl-2 border-l border-stone-700 space-y-2 text-[10px] font-mono text-stone-400 max-h-60 overflow-y-auto custom-scrollbar">
                                        {msg.trace.steps.map((step, sIdx) => (
                                            <div key={sIdx} className="flex flex-col gap-0.5">
                                                <div className="flex justify-between text-stone-300">
                                                    <span className="font-bold text-purple-300">{step.stage}</span>
                                                    <span>+{step.relativeTime}ms</span>
                                                </div>
                                                <div className="text-stone-500">{step.action}</div>
                                                {step.details && Object.keys(step.details).length > 0 && (
                                                    <pre className="text-stone-600 overflow-x-auto whitespace-pre-wrap">
                                                        {JSON.stringify(step.details, null, 1).replace(/[{}"]/g, '')}
                                                    </pre>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    </div>
                ))}


                {isLoading && (
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-900/50 text-purple-300 flex items-center justify-center shrink-0 animate-pulse">
                            <Bot size={16} />
                        </div>
                        <div className="bg-stone-800/50 rounded-2xl px-4 py-3 border border-stone-700 rounded-tl-sm flex flex-col gap-2">
                            <div className="flex gap-1 items-center">
                                <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                            <span className="text-xs text-stone-400 animate-pulse">
                                { /* Hardcoded specific message for UX as requested */}
                                Das KI-Modell wird geladen. Dies kann beim ersten Start kurz dauern...
                            </span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={`p-4 bg-[var(--color-bg-surface)] border-t border-[var(--color-border)] shrink-0 ${isEmbedded ? 'pb-4' : ''}`}>

                {/* Upload Menu */}
                {isUploadMenuOpen && (
                    <div className="absolute bottom-20 left-4 bg-stone-800 border border-stone-700 rounded-lg shadow-xl p-2 flex flex-col gap-2 z-50 animate-in fade-in slide-in-from-bottom-2">
                        <button onClick={handleFileUploadTrigger} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-700/50 rounded text-sm text-stone-300 transition-colors text-left">
                            <FileText size={16} className="text-blue-400" /> Upload File
                        </button>
                        <button onClick={handleFileUploadTrigger} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-700/50 rounded text-sm text-stone-300 transition-colors text-left">
                            <ImageIcon size={16} className="text-purple-400" /> Upload Image
                        </button>
                        <button className="flex items-center gap-2 px-3 py-2 hover:bg-stone-700/50 rounded text-sm text-stone-300 transition-colors text-left opacity-50 cursor-not-allowed">
                            <LinkIcon size={16} className="text-green-400" /> Insert Link
                        </button>
                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                    </div>
                )}

                <div className="relative flex items-center gap-2">
                    {/* Plus Button */}
                    <button
                        onClick={() => setIsUploadMenuOpen(!isUploadMenuOpen)}
                        className={`p-2 rounded-full transition-colors shrink-0 ${isUploadMenuOpen ? 'bg-stone-700 text-white' : 'bg-stone-800/50 text-stone-400 hover:bg-stone-700 hover:text-white'}`}
                    >
                        <Plus size={20} />
                    </button>

                    <div className="relative flex-1">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask or upload..."
                            className="w-full bg-stone-900/50 border border-stone-700 rounded-2xl pl-5 pr-20 py-4 text-sm text-stone-200 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50 transition-all placeholder:text-stone-600 resize-none h-24 scrollbar-thin scrollbar-thumb-stone-700 scrollbar-track-transparent"
                            disabled={isLoading}
                        />

                        {/* Mic & Send Buttons inside Input */}
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            <button
                                onClick={handleSpeech}
                                className={`p-2 rounded-full transition-colors ${isListening ? 'bg-red-500/20 text-red-500 animate-pulse' : 'text-stone-400 hover:text-purple-400 hover:bg-purple-900/20'}`}
                                title="Speech to Text"
                            >
                                <Mic size={18} />
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={!input.trim() || isLoading}
                                className="p-2 bg-purple-600 hover:bg-purple-500 text-white rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showSyncConfirm}
                onClose={() => setShowSyncConfirm(false)}
                onConfirm={executeSync}
                title="Sync Knowledge Base?"
                message="This will re-index all products for the AI. This process might take a while depending on the number of products. Are you sure you want to continue?"
                confirmText="Start Sync"
            />
        </div>
    );
};

